import { useGameStore } from "../store/gameStore";
import { useUIStore } from "../store/uiStore";
import { BuildingSlot } from "../components/city/BuildingSlot";
import { BuildingPanel } from "../components/panels/BuildingPanel";
import { WorkersPanel } from "../components/panels/WorkersPanel";
import { hapticFeedback } from "../telegram/init";

export function CityScreen() {
  const cities = useGameStore((s) => s.cities);
  const activeCityId = useGameStore((s) => s.activeCityId);
  const activePanel = useUIStore((s) => s.activePanel);
  const openPanel = useUIStore((s) => s.openPanel);

  const city = cities.find((c) => c.id === activeCityId);
  if (!city) {
    return (
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "var(--space-lg)",
        padding: "var(--space-xl)",
      }}>
        <span style={{ fontSize: "3rem" }}>{"\u{1F3DB}\uFE0F"}</span>
        <p style={{
          fontFamily: "var(--font-display)",
          color: "var(--ink-faded)",
          textAlign: "center",
        }}>
          No city found. Start your journey!
        </p>
      </div>
    );
  }

  // Build an array of 10 slots, populated with buildings where they exist
  const slots = Array.from({ length: 10 }, (_, i) => {
    const building = city.buildings.find((b) => b.position === i);
    return { position: i, building };
  });

  const idleCitizens = Math.floor(city.populationCurrent) - city.forestWorkers - city.mineWorkers - city.scientists;

  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      {/* — City header — */}
      <div className="meander-border" style={{
        padding: "var(--space-md) var(--space-lg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "linear-gradient(135deg, rgba(201,169,97,0.08), transparent)",
      }}>
        <div>
          <h2 style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.1rem",
            color: "var(--ink)",
            display: "flex",
            alignItems: "center",
            gap: "var(--space-sm)",
          }}>
            {city.isCapital && <span style={{ fontSize: "0.9rem" }}>{"\u{1F451}"}</span>}
            {city.name}
          </h2>
          <span style={{
            fontSize: "0.75rem",
            color: "var(--ink-faded)",
          }}>
            {city.islandName ?? "Unknown Island"} {" \u2022 "} {city.islandLuxury ?? ""}
          </span>
        </div>

        {/* Workers button */}
        <button
          onClick={() => {
            hapticFeedback("medium");
            openPanel("workers");
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-xs)",
            padding: "6px 12px",
            borderRadius: "20px",
            background: "var(--parchment)",
            border: "1px solid var(--sand)",
            fontSize: "0.75rem",
            fontFamily: "var(--font-display)",
            color: "var(--ink-light)",
          }}
        >
          <span>{"\u{1F465}"}</span>
          <span style={{ fontWeight: 700 }}>
            {Math.floor(city.populationCurrent)}/{city.populationMax}
          </span>
          <span style={{ color: "var(--ink-faded)", fontSize: "0.65rem" }}>
            ({idleCitizens} idle)
          </span>
        </button>
      </div>

      {/* — Building grid — */}
      <div style={{
        flex: 1,
        overflow: "auto",
        padding: "var(--space-lg)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-lg)",
      }}>
        {/* Main building area */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
          gap: "var(--space-sm)",
          maxWidth: "440px",
          margin: "0 auto",
          width: "100%",
        }}>
          {slots.map(({ position, building }) => (
            <BuildingSlot
              key={position}
              position={position}
              building={building}
              onClick={(pos) => {
                hapticFeedback("medium");
                openPanel("building", pos);
              }}
            />
          ))}
        </div>

        {/* Happiness & population quick stats */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: "var(--space-xl)",
          padding: "var(--space-md)",
          background: "linear-gradient(135deg, var(--marble), var(--papyrus-light))",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--sand)",
        }}>
          <QuickStat
            icon={"\u{1F60A}"}
            label="Happiness"
            value={Math.floor(city.happiness)}
            color={city.happiness >= 100 ? "var(--olive)" : "var(--terra)"}
          />
          <QuickStat
            icon={"\u{1F332}"}
            label="Woodcutters"
            value={city.forestWorkers}
          />
          <QuickStat
            icon={"\u26CF\uFE0F"}
            label="Miners"
            value={city.mineWorkers}
          />
          <QuickStat
            icon={"\u{1F4DA}"}
            label="Scientists"
            value={city.scientists}
          />
        </div>
      </div>

      {/* — Panels — */}
      {activePanel === "building" && <BuildingPanel />}
      {activePanel === "workers" && <WorkersPanel />}
    </div>
  );
}

function QuickStat({ icon, label, value, color }: {
  icon: string; label: string; value: number; color?: string;
}) {
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "1px",
    }}>
      <span style={{ fontSize: "0.95rem" }}>{icon}</span>
      <span style={{
        fontFamily: "var(--font-display)",
        fontSize: "0.85rem",
        fontWeight: 700,
        color: color ?? "var(--ink)",
      }}>
        {value}
      </span>
      <span style={{
        fontFamily: "var(--font-display)",
        fontSize: "0.45rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "var(--ink-faded)",
      }}>
        {label}
      </span>
    </div>
  );
}
